import { PaletteOptions } from '@mui/material/styles'
import { batteryWidget, colors, costSavingsChart, eMobilityChartColors, liveView, systemChartColors, wallboxWidget } from './config'
import { baseColors } from '../base/config'

/**
 * Flow light theme palette.
 */
export const flowLightPalette: PaletteOptions = {
  shades: {
    shade01: baseColors.grey.grey04,
    shade02: baseColors.grey.grey05,
    shade03: baseColors.grey.grey06,
    shade04: baseColors.grey.grey07,
  },
  background: {
    paperAccent: colors.primary.main,
    weekDayButtonActive: colors.primary.main,
    sidebar: colors.black,
    paper2: baseColors.grey.grey07,
    chip: baseColors.grey.grey06,
    paperBorder: baseColors.grey.grey06,
    sidebarProfile: colors.primary.dark,
    header: colors.white,
    application: baseColors.grey.grey15,
    warrantyBubbles: baseColors.grey.grey05,
    weekDayButtonInactive: baseColors.grey.grey06,
    layoutEditorItem: baseColors.grey.grey06,
    changedInputField: colors.white,
  },
  liveView: liveView,
  map: {
    editButton: colors.primary.main,
  },
  primary: {
    light: colors.primary.light,
    main: colors.primary.main,
    dark: colors.primary.dark,
    contrastText: colors.text.contrastText,
  },
  secondary: {
    light: colors.primary.light,
    main: colors.primary.main,
    dark: colors.primary.dark,
    contrastText: colors.text.contrastText,
  },
  error: {
    main: baseColors.error.main,
    light: baseColors.error.light,
    dark: baseColors.error.main,
  },
  warning: {
    light: baseColors.warning.light,
    main: baseColors.warning.main,
    dark: baseColors.warning.dark,
    contrastText: colors.text.contrastText,
  },
  text: {
    primary: baseColors.grey.grey00,
    paperTitle: baseColors.grey.grey12,
    secondary: baseColors.grey.grey05,
    primaryDisabled: '#6666667d',
    header: baseColors.grey.grey00,
    headerTitle: baseColors.grey.grey04,
    sidebar: baseColors.grey.grey03,
  },
  action: {
    active: colors.primary.main,
  },
  accordion: {
    border: baseColors.grey.grey06,
    title: baseColors.grey.grey00,
    text: baseColors.grey.grey12,
  },
  fileButton: {
    background: baseColors.grey.grey10,
    border: colors.white,
    iconColor: baseColors.grey.grey00,
    boxShadow: '-5px 5px 10px #c4c4c4, 5px -5px 10px #ffffff',
  },
  filter: {
    activeBackground: colors.primary.light,
    activeBorder: colors.primary.main,
    activeText: colors.primary.main,
    menuItemHoverBackground: colors.primary.light,
    inactiveBackground: baseColors.grey.grey07,
    hoverBorder: baseColors.grey.grey00,
    inactiveBorder: baseColors.grey.grey06,
    inactiveText: baseColors.grey.grey00,
    searchFieldBackground: baseColors.grey.grey07,
    selectedMenuItemHoverBackground: colors.primary.light, // as there is a global menu item hover bg color override, here we revert it to what is autocalculated when the override is removed
  },
  buttonGroup: {
    activeBackground: colors.primary.light,
    activeColor: colors.primary.main,
    borderColorActive: colors.primary.main,
    inactiveBackground: colors.white,
    inactiveColor: baseColors.grey.grey04,
    borderColor: baseColors.grey.grey05,
    activeIcon: baseColors.grey.grey13,
    inactiveIcon: 'transparent',
    iconColor: baseColors.grey.grey14,
    hover: baseColors.yellow.neon,
    borderWidth: '1px',
    borderWidthInactive: '1px',
  },
  chart: {
    background: 'transparent',
    totalEnergyChargedBar: eMobilityChartColors.totalEnergyChargedBar,
    totalGridChargedBarV1: eMobilityChartColors.totalGridChargedBarV1,
    totalGridChargedBar: eMobilityChartColors.totalGridChargedBar,
    totalGridChargedBarLightestShade: eMobilityChartColors.totalGridChargedBarLightestShade,
    totalChargedBackground: eMobilityChartColors.totalChargedBackground,
    totalDischargedEnergyCardBackground: eMobilityChartColors.totalDischargedEnergyCardBackground,
    totalDischargedEnergyBackgroundV1: eMobilityChartColors.totalDischargedEnergyBackgroundV1,
    totalDischargedEnergyBackground: eMobilityChartColors.totalDischargedEnergyBackground,
    solarChargedBarV1: eMobilityChartColors.solarChargedBarV1,
    solarChargedBar: eMobilityChartColors.solarChargedBar,
    solarChargedBarLightestShade: eMobilityChartColors.solarChargedBarLightestShade,
    solarChargedBackground: eMobilityChartColors.solarChargedBackground,
    solarStatCardIcon: eMobilityChartColors.solarStatCardIcon,
    chargeDurationCardBackground: eMobilityChartColors.chargeDurationCardBackground,
    dynamicTariffChartAreaBackgroundColor: eMobilityChartColors.dynamicTariffChartAreaBackgroundColor,
    dynamicTariffChartAreaStrokeColor: eMobilityChartColors.dynamicTariffChartAreaStrokeColor,
    dynamicTariffChartDotColor: eMobilityChartColors.dynamicTariffChartDotColor,
    dynamicTariffChartReferenceLineColor: eMobilityChartColors.dynamicTariffChartReferenceLineColor,
  },
  select: {
    optionActive: baseColors.blue.light,
    optionFocused: baseColors.blue.light,
    optionSelected: baseColors.blue.light,
  },
  sidebar: {
    active: colors.white,
    inactive: baseColors.grey.grey05,
  },
  success: {
    main: colors.primary.main,
    dark: colors.primary.dark,
  },
  purple: {
    light: colors.secondary.light,
    dark: colors.secondary.dark,
    main: colors.secondary.main,
    ultraLight: colors.primary.light,
    contrastText: colors.text.contrastText,
  },
  yellow: {
    light: baseColors.yellow.light,
    main: baseColors.yellow.main,
    dark: baseColors.yellow.dark,
  },
  olive: {
    light: baseColors.olive.light,
    main: baseColors.olive.main,
    dark: baseColors.olive.dark,
    contrastText: colors.text.contrastText,
  },
  ocppConnectorStatusColor: {
    available: baseColors.occpColors.available,
    preparing: baseColors.occpColors.preparing,
    charging: baseColors.occpColors.charging,
    suspendedEVSE: baseColors.occpColors.suspendedEVSE,
    suspendedEV: baseColors.occpColors.suspendedEV,
    finishing: baseColors.occpColors.finishing,
    reserved: baseColors.occpColors.reserved,
    unavailable: baseColors.occpColors.unavailable,
    undefined: baseColors.occpColors.undefined,
    faulted: baseColors.occpColors.faulted,
    occupied: baseColors.occpColors.occupied,
  },
  blue: {
    light: baseColors.blue.light,
    main: baseColors.blue.main,
    dark: baseColors.blue.dark,
    sky: baseColors.blue.sky,
    deep: baseColors.blue.deep,
  },
  lime: {
    light: baseColors.lime,
    main: baseColors.lime,
    dark: baseColors.lime,
  },
  leaf: {
    light: colors.primary.main,
    main: colors.primary.main,
    dark: colors.primary.main,
  },
  mint: {
    light: baseColors.mint,
    main: baseColors.mint,
    dark: baseColors.mint,
  },
  teal: {
    light: baseColors.teal,
    main: baseColors.teal,
    dark: baseColors.teal,
  },
  magenta: {
    light: baseColors.magenta,
    main: baseColors.magenta,
    dark: baseColors.magenta,
  },
  pink: {
    light: baseColors.pink,
    main: baseColors.pink,
    dark: baseColors.pink,
  },
  grey: {
    grey00: baseColors.grey.grey00,
    grey01: baseColors.grey.grey01,
    grey02: baseColors.grey.grey02,
    grey03: baseColors.grey.grey03,
    grey04: baseColors.grey.grey04,
    grey05: baseColors.grey.grey05,
    grey06: baseColors.grey.grey06,
    grey07: baseColors.grey.grey07,
    grey08: baseColors.grey.grey08,
    grey09: baseColors.grey.grey09,
    grey10: baseColors.grey.grey10,
    grey11: baseColors.grey.grey11,
    shadow: baseColors.shadow,
    shadowLight: colors.white,
    inputBorder: baseColors.grey.grey05,
    inputBorderDisabled: baseColors.grey.grey05,
  },
  storageType: {
    SERIE: baseColors.storageType.serie,
    SERIE_M_WR: baseColors.storageType.serieMWr,
    FARM: baseColors.storageType.farm,
    'FARM-ENGINEERING': baseColors.storageType.farm,
    ENGENEERING: baseColors.storageType.engineering, //TYPO in DB....
    FELDTEST: baseColors.storageType.fieldTest,
    NONE: baseColors.storageType.none,
    MESSE: baseColors.storageType.fair,
  },
  dirtyInput: {
    main: colors.dirtyInput,
  },
  chargingSessions: {
    statusChip: {
      active: {
        background: '#ADE2F2',
        text: '#0DB2C7',
      },
      invalid: {
        background: '#FFCCBC',
        text: '#BF360C',
      },
      pending: {
        background: '#EBDA2F',
        text: '#4a4a4a',
      },
      completed: {
        background: '#D7E9BB',
        text: '#5C9C11',
      },
      unknown: {
        background: '#e6e6e6',
        text: '#4a4a4a',
      },
    },
  },
  chargingCard: {
    status: {
      active: {
        background: '#D7E9BB',
        text: '#5C9C11',
      },
      inactive: {
        background: '#E0E0E0',
        text: '#212121',
      },
      rejected: {
        background: '#FFCCBC',
        text: '#BF360C',
      },
    },
  },
  tariff: {
    status: {
      active: {
        background: '#79B41C3D',
        text: '#79B41C',
      },
      inactive: {
        background: '#00000014',
        text: '#4A4A4A',
      },
      expired: {
        background: '#BF360C3D',
        text: '#BF360C',
      },
    },
  },
  tourGuide: {
    containerColor: '#add8e6',
  },
  energyStorageManagementDescriptions: {
    solar: colors.hagerYellow,
    storage: colors.hagerBatteryRegular,
    grid: colors.primary.main,
  },
  productionPieChart: {
    SelfProduction: systemChartColors.SelfProduction,
    NetIn: systemChartColors.NetIn,
  },
  AutarkyPieChart: {
    NetOutWithoutThreeSixtySavings: systemChartColors.NetOutWithoutThreeSixtySavings,
    ThreeSixtySavings: systemChartColors.ThreeSixtySavings,
    Autarky: systemChartColors.Autarky,
  },
  savingsPieChart: {
    threeSixtySavingsBatteryColor: costSavingsChart.threeSixtySavingsBatteryColor,
    threeSixtySavingsWallboxColor: costSavingsChart.threeSixtySavingsWallboxColor,
  },
  costSavingsChart: costSavingsChart,
  dppWidget: {
    // TODO: These Colors needs to be checked when dpp widget is implemented.
    underTheThresholdColorFill: baseColors.warning.light,
    underTheThresholdColorStroke: baseColors.warning.main,
    overTheThresholdColorFill: colors.primary.light,
    overTheThresholdColorStroke: colors.primary.main,
    dynamicEmsColorFill: colors.purple.ultraLight,
    dynamicEmsColorStroke: baseColors.teal,
    batteryPriceLimitColor: colors.primary.dark,
    wallboxPriceLimitColor: colors.purple.main,
    consumptionPriceColor: costSavingsChart.consumptionPriceColor,
    dynamicEmsNormalOperationColor: costSavingsChart.dynamicEmsNormalOperationColor,
    dynamicEmsGridChargeColor: costSavingsChart.dynamicEmsGridChargeColor,
    dynamicEmsBlockDischargingColor: costSavingsChart.dynamicEmsBlockDischargingColor,
  },
  pvForecastWidgetChart: {
    pvForecastInactiveColor: baseColors.grey.grey06,
    pvForecastActiveColor: baseColors.yellow.dark,
    pvForecastStrokeLineColor: baseColors.yellow.dark,
  },
  systemChartColors: systemChartColors,
  wallboxWidget: wallboxWidget,
  batteryWidget: batteryWidget,
  sgReadyModes: {
    NORMAL: { backgroundColor: '#BEE7FF', color: '#004673' },
    RECOMMENDED: { backgroundColor: '#45D1DB80', color: '#004673' },
    FORCED: { backgroundColor: '#45D1DB', color: '#004673' },
    BLOCKED: { backgroundColor: '#FFAA6680', color: '#CC4400' },
  },
}
